import { CalendarDays, MapPin } from 'lucide-react';
import { festivalStatus, formatDateRange, useNow } from '@/lib/timetable';

interface FestivalHeaderProps {
  name: string;
  startTime: string;
  endTime: string;
  location?: string | null;
  /** Small label shown above the name, e.g. "Alice's shifts". */
  eyebrow?: string;
}

export function FestivalHeader({ name, startTime, endTime, location, eyebrow }: FestivalHeaderProps) {
  const now = useNow();
  const status = festivalStatus(now, startTime, endTime);

  const toneClass =
    status.state === 'during'
      ? 'border-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
      : status.state === 'before'
        ? 'border-sky-500/30 bg-sky-500/15 text-sky-700 dark:text-sky-300'
        : 'border-border bg-muted text-muted-foreground';

  return (
    <div className="space-y-2">
      {eyebrow && <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{eyebrow}</p>}
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-3xl font-bold">{name}</h1>
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${toneClass}`}>
          {status.state === 'during' && <span className="h-1.5 w-1.5 rounded-full bg-current motion-safe:animate-pulse" />}
          {status.label}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <CalendarDays className="h-4 w-4" />
          {formatDateRange(startTime, endTime)}
        </span>
        {location && (
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="h-4 w-4" />
            {location}
          </span>
        )}
      </div>
    </div>
  );
}
